import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQcVisitState } from './useQcVisitState';
import { qcErrorMessage } from '../../../services/qcApiError';
import { QcRequestState } from './qcRequestState';
import { Helmet } from 'react-helmet';
import { Box, Grid, Snackbar, Stack, Typography, useTheme } from '@mui/material';
import { Alert } from '@mui/lab';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import axios from 'axios';
import { LocalizeContextProps, Translate, withLocalize } from 'react-localize-redux';
import Loader from '../../../components/Loader';
import SectionHeader from '../../components/SectionHeader';
import { ColourChip } from '../../../components/general/mini_components-ts';
import { PERMISSION } from '../../../components/investigation/share/user_roles';
import { FUNCTIONALITY } from '../../../constants/types';
import { HOSPITAL_QUALITY_CHECK, ROUTE_401 } from '../../../routes/urls';
import { qualityCheckServices, QcVisitStateDto } from '../../../services/qualityCheck';
import LesionReviewSection from './LesionReviewSection';
import VisitLevelDiagnoses from './VisitLevelDiagnoses';
import VisitShotsPanel from './VisitShotsPanel';
import ReviewRail, { ApprovalState } from './ReviewRail';
import CorrectIdentifiableModal from './CorrectIdentifiableModal';
import ApprovalSuccess from './ApprovalSuccess';
import { computeVisitProgress } from './reviewProgress';
import { STATE_PALETTE, shortVisitCode, stateTranslateId } from './visitQueueRow';
import { ImageAssessmentValue } from './ImageAssessment';
import { LesionFeedbackValue } from './LesionPanel';
import { ReviewImage, groupImagesByLesion } from './imageMeta';
import { QcCategoriesState } from './qcCategories';
import {
    emptyAssessment,
    emptyLesionValue,
    lesionCategoriesFromVisit,
    toVisitShots,
} from './mapQcVisitState';
import { useIdentifiableCorrection } from './useIdentifiableCorrection';
import { usePerImageDecrypt } from './usePerImageDecrypt';
import { useQcCategoriesCache } from './useQcCategoriesCache';
import { useSnackBarState } from '../../../hooks/useSnackBarState';
import CorrectionFlagBanner from './CorrectionFlagBanner';
import CorrectionFlagModal from './CorrectionFlagModal';
import { useCorrectionFlagStep } from './useCorrectionFlagStep';
import { canUseCorrectionFlag } from './correctionFlag';
import { buildVisitWithoutImagesLink } from '../AnalyticsBeta/buildVisitWithoutImagesLink';

interface VisitDetailProps {
    /**
     * The active language code. Required, and deliberately not defaulted: it exists so the
     * approval date is not formatted in the browser's locale, and a `?? 'en'` fallback would
     * hide exactly that bug. `withLocalize` supplies it on the default export.
     */
    localeCode: string;
    investigations?: {
        loading?: boolean;
        currentInvestigation?: {
            uuid: string;
            permissions?: string[];
            functionalities?: string[];
        };
    };
}

/**
 * Visit review container: load state, decrypt per image, upsert assessments, carry the
 * rail that lets a reviewer finish (SKIN-277), and own the correction of an image's
 * identifiable flag (SKIN-243).
 */
export function VisitDetail({ localeCode, ...props }: VisitDetailProps) {
    const theme = useTheme();
    const history = useHistory();
    const { uuidVisit } = useParams<{ uuidVisit: string }>();
    const investigation = props.investigations?.currentInvestigation;
    const canReview = investigation?.permissions?.includes(PERMISSION.APPROVE_VISIT);
    const isClinical = investigation?.functionalities?.includes(FUNCTIONALITY.CLINICAL);
    const canViewIdentifiable = !!investigation?.permissions?.includes(
        PERMISSION.VIEW_IDENTIFIABLE_IMAGES,
    );
    const authorized = !!(canReview && isClinical);
    /** SKIN-577 — the study's correction switch, on top of the Quality Check gate. */
    const correctionEnabled = authorized && canUseCorrectionFlag(investigation);

    const [loadState, setLoadState] = useState<'loading' | 'ready' | 'failed'>('loading');
    const [loadError, setLoadError] = useState<string | null>(null);
    /** Everything a loaded visit means — payload, reviewer overrides and their derivations. */
    const {
        visit,
        baseImages,
        corrections,
        assessments,
        lesionValues,
        undiagnosedLesions,
        workupByLesion,
        lesionCounts,
        assessmentWrites,
        lesionWrites,
        applyVisit,
        applyCorrection,
        applyWrittenFlag,
        markApproved,
        applyFlag,
    } = useQcVisitState();

    const [showSnackbar, setShowSnackbar, handleCloseSnackbar] = useSnackBarState();
    /**
     * Where the approve request itself has got to, as distinct from whether the visit is
     * approved — that lives on `visit.state`, which the server owns. `submitting` and
     * `failed` are facts about this click and nothing else.
     */
    const [approveRequest, setApproveRequest] = useState<QcRequestState>({ status: 'editable' });
    /** Show the confirmation instead of the review. Only ever set by a successful approve. */
    const [justApproved, setJustApproved] = useState(false);

    useEffect(() => {
        if (!authorized || !investigation?.uuid || !uuidVisit) return;
        const source = axios.CancelToken.source();
        setLoadState('loading');
        setLoadError(null);
        qualityCheckServices.getVisitQcState(investigation.uuid, uuidVisit, source.token)
            .then((payload) => {
                applyVisit(payload);
                setLoadState('ready');
            })
            .catch((err) => {
                if (axios.isCancel(err)) return;
                setLoadError(qcErrorMessage(err, 'Failed to load visit'));
                setLoadState('failed');
            });
        return () => source.cancel('superseded');
    }, [authorized, investigation?.uuid, uuidVisit, applyVisit]);

    // SKIN-618 — the healthy-skin shot is shown for context, so its pixels are fetched
    // too, though it is never among the photographs to mark.
    const visitShots = useMemo(
        () => toVisitShots(visit?.requiredVisitShots, visit?.images ?? []),
        [visit?.requiredVisitShots, visit?.images],
    );
    const decryptTargets = useMemo(
        () => [
            ...baseImages,
            ...visitShots.flatMap((shot) => (shot.image ? [shot.image] : [])),
        ].map((image) => ({
            encryptedFileUuid: image.encryptedFileUuid,
            localId: image.localId,
        })),
        [baseImages, visitShots],
    );
    const { bytesByUuid, retry } = usePerImageDecrypt(
        authorized ? investigation?.uuid : undefined,
        decryptTargets,
    );

    const fieldNames = useMemo(
        () => Array.from(new Set(baseImages.map((image) => image.fieldName))),
        [baseImages],
    );
    const categoriesByField = useQcCategoriesCache(
        authorized ? investigation?.uuid : undefined,
        fieldNames,
    );

    const images: ReviewImage[] = useMemo(
        () => baseImages.map((image) => ({
            ...image,
            bytes: bytesByUuid[image.encryptedFileUuid] ?? image.bytes,
        })),
        [baseImages, bytesByUuid],
    );

    // Same overlay as `images`, for the visit shots' pixels (SKIN-618).
    const shots = useMemo(
        () => visitShots.map((shot) => (shot.image
            ? { ...shot, image: { ...shot.image, bytes: bytesByUuid[shot.image.encryptedFileUuid] ?? shot.image.bytes } }
            : shot)),
        [visitShots, bytesByUuid],
    );

    const lesions = useMemo(() => groupImagesByLesion(images), [images]);
    // One statement of "the review is on screen", read by both blocks below. Spelled
    // twice, the two guards have to be diffed to see they mean the same thing.
    const showingReview = loadState === 'ready' && !justApproved;
    const readOnly = visit?.state === 'approved';

    /**
     * The one projection both the rail and the confirmation read. Derived here rather than
     * counted again in either of them, so the number the reviewer is asked to trust cannot
     * disagree with the cards they can see.
     */
    const progress = useMemo(
        () => computeVisitProgress({
            images,
            assessmentFor: (uuid) => assessments[uuid] ?? emptyAssessment(),
            lesionFeedback: Object.values(lesionValues),
        }),
        [images, assessments, lesionValues],
    );

    const approval: ApprovalState = readOnly
        // The API does not serve who approved it or when — `QcVisitStateDto` carries none of
        // that — so the rail states the lock and omits the attribution rather than inventing
        // one. Falling back to the current user would be worse than silence: they are not
        // necessarily the approver.
        ? { status: 'approved', approvedBy: null, approvedAt: null }
        : approveRequest.status === 'submitting'
            ? { status: 'submitting' }
            : approveRequest.status === 'failed'
                ? { status: 'failed', error: approveRequest.error }
                : { status: 'editable' };

    const onApprove = useCallback(async () => {
        if (!investigation?.uuid || !uuidVisit) return;
        setApproveRequest({ status: 'submitting' });
        try {
            await qualityCheckServices.approveVisit(investigation.uuid, uuidVisit);
            markApproved();
            setApproveRequest({ status: 'editable' });
            setJustApproved(true);
        } catch (err: any) {
            // Nothing is lost: the marks were written by their own endpoints and approval
            // refuses a visit whose outcomes are not already stored. The banner says so.
            setApproveRequest({
                status: 'failed',
                error: qcErrorMessage(err, 'Approve failed'),
            });
        }
    }, [investigation?.uuid, uuidVisit]);
    const lesionCategories = visit
        ? lesionCategoriesFromVisit(visit)
        : ({ status: 'loading' } as QcCategoriesState);

    /**
     * The correction is offered only to a reviewer who may see identifying features, and
     * only while the visit is open. The server enforces both — it refuses the correction
     * without the permission, and refuses any write to an approved visit — so withholding
     * the handler is about not offering an action that is going to be refused.
     *
     * Withheld, not disabled: §5 of the design brief shows capabilities progressively.
     */
    const canCorrect = canViewIdentifiable && !readOnly;

    const correction = useIdentifiableCorrection({
        uuidInvestigation: investigation?.uuid,
        uuidVisit,
        images,
        onWritten: applyWrittenFlag,
        onCorrected: applyCorrection,
    });

    const flagStep = useCorrectionFlagStep({
        uuidInvestigation: investigation?.uuid,
        uuidVisit,
        onApplied: applyFlag,
    });

    /**
     * The correction is made on the patient record, opened on this visit — the same URL the
     * Analytics "visits without images" link builds, so the two cannot disagree on it.
     */
    const openPatientRecord = useCallback(() => {
        if (!visit || !uuidVisit) return;
        history.push(buildVisitWithoutImagesLink({
            patientInvestigationUuid: visit.patientInvestigationUuid,
            visitUuid: uuidVisit,
        }));
    }, [visit, uuidVisit, history]);

    const showSaveError = useCallback((message: string) => {
        setShowSnackbar({ show: true, message, severity: 'error' });
    }, [setShowSnackbar]);

    const onAssessmentChange = useCallback(async (
        encryptedFileUuid: string,
        next: ImageAssessmentValue,
    ) => {
        if (!investigation?.uuid || !uuidVisit || readOnly) return;
        /**
         * A cleared verdict is not offered, and this is the reason rather than an
         * oversight. MUI's exclusive ToggleButtonGroup emits null when the reviewer
         * clicks the button already selected, but the API refuses anything that is not
         * `correct` or `needs` (qc-feedback/entries.ts, upsertImageQcEntry). Passing the
         * null through would trade a dead click for a 400 and an error toast. The
         * assessment control is fully controlled by this state, so returning here leaves
         * the button selected and the screen agreeing with the server.
         */
        if (!next.outcome) return;
        // Bound outside the closure below: TypeScript drops the narrowing from that
        // guard once `next` is read inside a callback, since a parameter is a mutable
        // binding.
        const outcome = next.outcome;
        await assessmentWrites.enqueue({
            key: encryptedFileUuid,
            next,
            write: () => qualityCheckServices.upsertImageQcEntry(
                investigation.uuid,
                uuidVisit,
                encryptedFileUuid,
                {
                    outcome,
                    categoryIds: outcome === 'needs' ? next.categoryIds : [],
                    comment: outcome === 'needs' ? next.comment : '',
                },
            ),
            onError: (err) => {
                showSaveError('hospital.quality-check.review.image-save-failed');
                // eslint-disable-next-line no-console
                console.error('QC image upsert failed', qcErrorMessage(err, 'no message'));
            },
        });
    }, [investigation?.uuid, uuidVisit, readOnly, assessmentWrites, showSaveError]);

    const onLesionChange = useCallback(async (
        groupRecordUuid: string,
        next: LesionFeedbackValue,
    ) => {
        if (!investigation?.uuid || !uuidVisit || readOnly) return;
        await lesionWrites.enqueue({
            key: groupRecordUuid,
            next,
            write: () => qualityCheckServices.upsertLesionQcEntry(
                investigation.uuid,
                uuidVisit,
                groupRecordUuid,
                { categoryIds: next.categoryIds, comment: next.comment },
            ),
            onError: (err) => {
                showSaveError('hospital.quality-check.review.lesion-save-failed');
                // eslint-disable-next-line no-console
                console.error('QC lesion upsert failed', qcErrorMessage(err, 'no message'));
            },
        });
    }, [investigation?.uuid, uuidVisit, readOnly, lesionWrites, showSaveError]);

    if (!props.investigations || props.investigations.loading || !investigation) {
        return <Loader />;
    }

    if (!authorized) {
        history.push(ROUTE_401);
        return <Loader />;
    }

    return (
        <React.Fragment>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={showSnackbar.show}
                autoHideDuration={2500}
                onClose={handleCloseSnackbar}
            >
                <div>
                    {showSnackbar.message && showSnackbar.severity && (
                        <Alert onClose={handleCloseSnackbar} severity={showSnackbar.severity}>
                            <Translate id={showSnackbar.message} />
                        </Alert>
                    )}
                </div>
            </Snackbar>
            <Helmet title="Quality Check" />
            <Grid container spacing={6}>
                <Grid item xs={12} style={{ color: 'white' }}>
                    <SectionHeader section="quality-check" edit={false} />
                </Grid>
                <Grid item xs={12} style={{ background: 'white', padding: '2rem' }}>
                    <Box sx={{ mb: 2 }}>
                        <Typography
                            component="button"
                            variant="body2"
                            onClick={() => history.push(HOSPITAL_QUALITY_CHECK)}
                            sx={{
                                border: 0,
                                background: 'none',
                                padding: 0,
                                cursor: 'pointer',
                                color: 'primary.main',
                                textDecoration: 'underline',
                            }}
                        >
                            <Translate id="hospital.quality-check.review.back-to-queue" />
                        </Typography>
                    </Box>

                    {/*
                      * The screen has to say which visit it is. It shipped with no title at
                      * all, so a reviewer arriving by URL — or holding two visits open in two
                      * tabs — had nothing on the page identifying whose photographs were in
                      * front of them.
                      *
                      * The code is the visit half only (`V6A32724B`) where the queue shows
                      * `P52-V6A32724B`: the QC read carries no patient identifier and the API
                      * is zero-knowledge, so the prefix is not derivable here. Both come out
                      * of `shortVisitCode`, so this is a subset of the queue's label rather
                      * than a competing name for the same visit.
                      *
                      * `component="h4"`: the dashboard's SectionHeader renders `h3`, and every
                      * heading below it was jumping straight to `h6`.
                      */}
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="h5" component="h4" data-testid="visit-title">
                            <Translate id="hospital.quality-check.review.visit-title" />
                            {` ${shortVisitCode(uuidVisit)}`}
                        </Typography>
                        {/* Same palette and same copy as the queue row, from one table. */}
                        {visit && (
                            <ColourChip
                                size="small"
                                data-testid="visit-state-chip"
                                rgbcolor={theme.palette[STATE_PALETTE[visit.state]].main}
                                label={<Translate id={stateTranslateId(visit.state)} />}
                            />
                        )}
                    </Stack>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ maxWidth: 600, mt: 0.5, mb: 3 }}
                        data-testid="review-intro"
                    >
                        <Translate id="hospital.quality-check.review.intro" />
                    </Typography>

                    {loadState === 'loading' && <Loader />}
                    {loadState === 'failed' && (
                        <Typography color="error">{loadError}</Typography>
                    )}
                    {loadState === 'ready' && lesions.length === 0 && (
                        <Typography>
                            <Translate id="hospital.quality-check.review.empty-visit" />
                        </Typography>
                    )}

                    {/*
                      * The confirmation replaces the review rather than sitting above it. The
                      * visit behind it is LOCKED, so leaving the cards on screen would offer
                      * a reviewer controls that have all just become static text.
                      */}
                    {loadState === 'ready' && justApproved && (
                        <ApprovalSuccess
                            progress={progress}
                            onBackToQueue={() => history.push(HOSPITAL_QUALITY_CHECK)}
                        />
                    )}

                    {/*
                      * SKIN-577 — first, because it changes what this review is for: a visit
                      * under correction is waiting for someone else, not for more marks.
                      */}
                    {showingReview && correctionEnabled && visit && (
                        <CorrectionFlagBanner
                            flag={visit.flag}
                            reasonSections={visit.visitCategorySections}
                            localeCode={localeCode}
                            onOpenPatientRecord={openPatientRecord}
                        />
                    )}

                    {/*
                      * SKIN-441 — the queue row said "3 of 7 undiagnosed" and sent the
                      * reviewer here; this repeats that number from the same server
                      * count, so the two surfaces never state different totals.
                      *
                      * It is not the number of chips below, and it is allowed to exceed
                      * them: sections render from photographs, so a lesion answered
                      * without one is counted here and has no section to be marked in.
                      * Saying the total out loud is what turns that from a silent
                      * discrepancy into something a reviewer can see.
                      */}
                    {showingReview && lesionCounts && lesionCounts.undiagnosedLesionCount > 0 && (
                        <Box
                            sx={{ mb: 2 }}
                            data-testid="visit-undiagnosed-summary"
                            // The numbers, readable without a translation provider. The
                            // copy resolves empty under the test provider, so asserting
                            // on it would pass whatever this was derived from — which is
                            // exactly the mistake this element exists to prevent.
                            data-undiagnosed={lesionCounts.undiagnosedLesionCount}
                            data-total={lesionCounts.lesionCount}
                        >
                            <Typography variant="body2" color="warning.main">
                                <Translate
                                    id="hospital.quality-check.review.undiagnosed-summary"
                                    data={{
                                        undiagnosed: lesionCounts.undiagnosedLesionCount,
                                        total: lesionCounts.lesionCount,
                                    }}
                                />
                            </Typography>
                        </Box>
                    )}

                    {/*
                      * SKIN-611 — a legacy visit-level D1, in its own block above the lesions
                      * and never inside one: it describes the visit, not any lesion.
                      */}
                    {showingReview && visit && (
                        <VisitLevelDiagnoses diagnoses={visit.visitLevelDiagnoses ?? []} />
                    )}

                    {/*
                      * SKIN-618 — the required visit-level shots (healthy skin), stated as
                      * present or missing above the lesions. Not reviewed: no verdict, and
                      * approval does not wait on them.
                      */}
                    {showingReview && (
                        <VisitShotsPanel readOnly={readOnly} shots={shots} />
                    )}

                    {showingReview && lesions.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                {lesions.map((lesion, index) => (
                                    <LesionReviewSection
                                        key={lesion.groupRecordUuid}
                                        groupRecordUuid={lesion.groupRecordUuid}
                                        lesionNumber={index + 1}
                                        undiagnosed={undiagnosedLesions.has(lesion.groupRecordUuid)}
                                        diagnosticWorkup={workupByLesion.get(lesion.groupRecordUuid) ?? null}
                                        images={lesion.images}
                                        canViewIdentifiable={canViewIdentifiable}
                                        readOnly={readOnly}
                                        corrections={corrections}
                                        onCorrectIdentifiable={canCorrect ? correction.open : undefined}
                                        onRetry={retry}
                                        lesionCategories={lesionCategories}
                                        lesionValue={lesionValues[lesion.groupRecordUuid] ?? emptyLesionValue()}
                                        onLesionChange={(next) => onLesionChange(lesion.groupRecordUuid, next)}
                                        categoriesFor={(image) =>
                                            categoriesByField[image.fieldName] ?? { status: 'loading' }}
                                        assessmentFor={(uuid) => assessments[uuid] ?? emptyAssessment()}
                                        onAssessmentChange={onAssessmentChange}
                                    />
                                ))}
                            </Box>
                            <ReviewRail
                                progress={progress}
                                canViewIdentifiable={canViewIdentifiable}
                                approval={approval}
                                localeCode={localeCode}
                                onApprove={onApprove}
                                correctionFlagState={visit?.flag.current?.state ?? null}
                                correctionActions={correctionEnabled && lesionCounts
                                    ? {
                                        undiagnosedLesionCount: lesionCounts.undiagnosedLesionCount,
                                        onStep: flagStep.open,
                                    }
                                    : undefined}
                            />
                        </Box>
                    )}

                    {/*
                      * Mounted once for the visit, not once per card: the dialog is modal,
                      * so only one photograph can be under correction at a time, and 21
                      * mounted dialogs would be 21 pieces of state that can disagree.
                      */}
                    {/* Keyed by step, so a dialog opened for another step starts empty. */}
                    {flagStep.step && visit && (
                        <CorrectionFlagModal
                            key={flagStep.step}
                            open
                            step={flagStep.step}
                            reasonSections={visit.visitCategorySections}
                            request={flagStep.request}
                            onConfirm={flagStep.confirm}
                            onCancel={flagStep.close}
                        />
                    )}

                    {correction.correctingImage && (
                        <CorrectIdentifiableModal
                            open
                            fieldName={correction.correctingImage.fieldName}
                            bodyRegion={correction.correctingImage.bodyRegion}
                            currentIdentifiable={correction.correctingImage.identifiable}
                            request={correction.request}
                            onConfirm={correction.confirm}
                            onCancel={correction.close}
                        />
                    )}
                </Grid>
            </Grid>
        </React.Fragment>
    );
}

const mapStateToProps = (state: any) => ({
    investigations: state.investigations,
});

/**
 * `withLocalize` wraps the connected container, not the exported `VisitDetail`.
 *
 * The unit tests render `VisitDetail` directly under a bare `LocalizeProvider`, which
 * registers no languages — `activeLanguage` would be `undefined` there and every test
 * touching the rail would die on `.code`. Wrapping out here keeps the component a plain
 * function of its props and matches `PatientAppointmentInfoCore` / `…Localized`.
 */
const VisitDetailLocalized = ({ activeLanguage, ...rest }: LocalizeContextProps & VisitDetailProps) => (
    // `activeLanguage.code` with no fallback, as `PatientAppointmentInfoCore` does. A
    // `?? 'en'` here would reintroduce the bug `localeCode` exists to fix, one level up and
    // silently — and an undefined active language is not a state the app can really be in
    // without every `<Translate>` on the page being broken too.
    <VisitDetail {...rest} localeCode={activeLanguage.code} />
);

export default connect(mapStateToProps)(withLocalize(VisitDetailLocalized));
